// Time-zone list shared by every timezone picker (web General settings + the
// install wizard). The product is deployed across the Gulf and the neighbouring
// Levant, so rather than surfacing the full ~400-entry IANA database — which
// buries the handful of zones any of these installs actually use — the picker
// offers a short, curated regional list:
//
//   Gulf (GCC): UAE, Saudi Arabia, Qatar, Kuwait, Bahrain, Oman
//   Iraq, Syria, Jordan, Turkey
//
// Each entry keeps its canonical IANA name as the value (the field hint still
// reads "IANA name, e.g. Asia/Dubai") with a country label so a non-technical
// admin can pick by country. A closed list also keeps the field un-typo-able,
// so a misspelled zone can never reach the day-rollup / report calculations.

/** Curated regional zones, in the order they appear in the picker. */
const REGIONAL_ZONES: ReadonlyArray<{ value: string; country: string }> = [
  { value: 'Asia/Dubai', country: 'United Arab Emirates' },
  { value: 'Asia/Riyadh', country: 'Saudi Arabia' },
  { value: 'Asia/Qatar', country: 'Qatar' },
  { value: 'Asia/Kuwait', country: 'Kuwait' },
  { value: 'Asia/Bahrain', country: 'Bahrain' },
  { value: 'Asia/Muscat', country: 'Oman' },
  { value: 'Asia/Baghdad', country: 'Iraq' },
  { value: 'Asia/Damascus', country: 'Syria' },
  { value: 'Asia/Amman', country: 'Jordan' },
  { value: 'Europe/Istanbul', country: 'Turkey' },
];

function label(zone: string, country: string): string {
  return `${zone} — ${country}`;
}

/**
 * Build the `{ value, label }` options for a time-zone select. `current` is
 * always included even if it isn't one of the curated regional zones, so an
 * already-saved (legacy or custom) zone is never dropped from the dropdown and
 * silently reset on the next save.
 */
export function timezoneOptions(
  current: string
): Array<{ value: string; label: string }> {
  const options = REGIONAL_ZONES.map((z) => ({
    value: z.value,
    label: label(z.value, z.country),
  }));
  if (current && !REGIONAL_ZONES.some((z) => z.value === current)) {
    options.unshift({ value: current, label: current });
  }
  return options;
}
